import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GithubMark, XMark } from "../BrandIcons";
import { fetchPublicProfile, formatRelative, shortAddr, type PublicIdentityProfile } from "../../lib/api";
import {
  fetchX402RecentReviews,
  reviewStableKey,
  type X402LinkedAccount,
  type X402RecentPublicReview,
  type X402RecentReviewItem,
} from "../../lib/x402Discovery";
import { serviceBrand, serviceDescription, serviceName } from "../../lib/serviceBranding";
import { ServiceBrand } from "./ServiceBrand";
import { preferredReviewerAccount } from "./X402ReviewerIdentities";

export interface RecentReview extends X402RecentReviewItem {
  profile: PublicIdentityProfile | null;
}

export function RecentX402Reviews({
  className = "mt-10",
  eyebrow = "public trail",
  heading,
  headingClassName = "text-2xl",
  groupByReviewer = false,
  limit = 3,
  showExploreLink = true,
  showHeader = true,
}: {
  className?: string;
  eyebrow?: string;
  heading?: string;
  headingClassName?: string;
  groupByReviewer?: boolean;
  limit?: number;
  showExploreLink?: boolean;
  showHeader?: boolean;
} = {}) {
  const [items, setItems] = useState<RecentReview[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [indexComplete, setIndexComplete] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setFailed(false);
    setItems(null);

    async function load() {
      try {
        const result = await fetchX402RecentReviews(limit, controller.signal);
        if (cancelled) return;
        // Optional profile enrichment must not hold the public reviews behind another request.
        setItems(result.items.map((item) => ({ ...item, profile: null })));
        setIndexComplete(result.pagination.indexComplete);
        const loaded = await Promise.all(result.items.map(async (item) => {
          const account = preferredReviewerAccount(
            item.profileAccounts.length > 0 ? item.profileAccounts : item.review.reviewer.accounts,
          );
          if (!account) return { ...item, profile: null };
          try {
            const response = await fetchPublicProfile(account.handle);
            return { ...item, profile: response.profile };
          } catch {
            return { ...item, profile: null };
          }
        }));
        if (!cancelled) {
          setItems(loaded);
          setIndexComplete(result.pagination.indexComplete);
        }
      } catch {
        if (!cancelled) {
          setItems([]);
          setFailed(true);
        }
      }
    }

    void load();
    return () => { cancelled = true; controller.abort(); };
  }, [limit, retry]);

  return (
    <section
      className={className}
      aria-labelledby={showHeader ? "recent-x402-reviews-heading" : undefined}
      aria-label={showHeader ? undefined : "Newest service reviews"}
    >
      {showHeader && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="btn-mono text-xs text-muted">{eyebrow}</p>
            <h2 id="recent-x402-reviews-heading" className={`mt-2 font-display tracking-tight text-ink ${headingClassName}`}>
              {heading ?? (indexComplete ? "Recent service reviews" : "Service reviews")}
            </h2>
          </div>
          {showExploreLink && (
            <Link className="btn-mono text-xs text-olive-deep hover:underline" to="/x402">
              Explore x402 tools →
            </Link>
          )}
        </div>
      )}

      {items === null && <p className="mt-4 text-sm text-muted">Loading recent reviews…</p>}
      {items !== null && items.length > 0 && (
        <div className="mt-5 grid gap-3">
          {groupByReviewer
            ? groupRecentReviewsByReviewer(items).map((group) => <RecentReviewerCard key={group.key} items={group.items} />)
            : items.map((item) => <RecentReviewCard key={reviewStableKey(item.review)} item={item} />)}
        </div>
      )}
      {items !== null && items.length === 0 && (
        <div className="mt-4 text-sm text-muted leading-relaxed" role={failed ? "alert" : "status"}>
          <p>{failed ? "We couldn’t load the recent reviews. Please try again." : recentReviewsEmptyMessage(indexComplete)}</p>
          {failed && <button type="button" className="btn btn-ghost mt-3 min-h-10" onClick={() => setRetry((value) => value + 1)}>Try again</button>}
        </div>
      )}
    </section>
  );
}

export function recentReviewsEmptyMessage(indexComplete: boolean): string {
  return indexComplete
    ? "No public service reviews were found."
    : "We couldn’t check every review source. No reviews are available to show right now.";
}

export function recentReviewerHref(item: X402RecentReviewItem): string {
  if (item.profileId) return `/x402/profiles/${item.profileId}`;
  const primary = preferredReviewerAccount(item.review.reviewer.accounts);
  return primary ? `/p/${encodeURIComponent(primary.handle)}` : `/x402/reviewers/${item.review.reviewer.wallet}`;
}

/**
 * Settlement-backed use can exist without a routed recognition record. Keep
 * those cards on the free task search instead of linking to the legacy route
 * detail, which correctly returns no recognition record for them.
 */
export function recentReviewRouteHref(item: X402RecentReviewItem): string {
  if (item.review.evidenceKind === "client_settlement" && item.route) {
    try {
      const endpoint = new URL(item.route.path, item.route.origin).toString();
      return `/x402?q=${encodeURIComponent(endpoint)}`;
    } catch {
      return "/x402";
    }
  }
  return `/x402/routes/${item.routeId}`;
}

function RecentReviewerCard({ items }: { items: RecentReview[] }) {
  const representative = items[0];
  if (!representative) return null;
  const profile = items.find((item) => item.profile)?.profile ?? null;
  const identityAccounts = uniqueLinkedAccounts(items.flatMap((item) =>
    item.profileAccounts.length > 0 ? item.profileAccounts : item.review.reviewer.accounts,
  ));
  const primary = preferredReviewerAccount(identityAccounts);
  const displayName = profile?.displayName || primary?.handle.replace(/^[^:]+:/, "") || shortAddr(representative.review.reviewer.wallet);
  const profileHref = recentReviewerHref(representative);

  return (
    <article className="min-w-0 overflow-hidden rounded-xl border border-faint bg-paper p-4 sm:p-5">
      <div className="flex min-w-0 items-start gap-3">
        <Link to={profileHref} aria-label={`Open ${displayName}'s profile`} className="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2">
          {profile?.avatarUrl ? (
            <img src={profile.avatarUrl} alt="" className="h-11 w-11 rounded-full border border-faint object-cover" />
          ) : (
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-olive/35 bg-olive-soft text-olive-deep" aria-hidden="true">
              <PersonGlyph className="h-5 w-5" />
            </span>
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Link className="min-w-0 break-words font-semibold text-ink hover:text-olive-deep hover:underline underline-offset-4" to={profileHref}>
              {displayName}
            </Link>
            <span className="flex max-w-full flex-wrap items-center gap-1" aria-label="Linked reviewer identities">
              {identityAccounts.map((account) => <ProviderBadge key={account.handle} account={account} showWallet={Boolean(representative.profileId)} />)}
            </span>
            <span className="pill pill-faint btn-mono text-[10px]">{items.length} {items.length === 1 ? "review" : "reviews"}</span>
          </div>
          <Link className="mt-1 inline-block btn-mono text-[10px] text-olive-deep hover:underline" to={profileHref}>
            View reviewer profile →
          </Link>
        </div>
      </div>

      <div className="mt-4 divide-y divide-faint border-t border-faint">
        {items.map((item) => (
          <div key={reviewStableKey(item.review)} className="py-4 first:pt-4 last:pb-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="pill pill-olive btn-mono text-[10px]">{humanEvidenceLabel(item.review)}</span>
              <span className="text-xs text-muted">Reviewed by {reviewerLabel(item)} · {reviewDate(item.review)}</span>
            </div>
            {serviceEndpoint(item) && <div className="mt-3"><ServiceBrand endpoint={serviceEndpoint(item)!} compact /></div>}
            <Link
              className="mt-2 block min-w-0 break-words [overflow-wrap:anywhere] font-semibold text-ink hover:text-olive-deep hover:underline underline-offset-4"
              to={recentReviewRouteHref(item)}
            >
              {recentReviewTitle(item)}
            </Link>
            <blockquote className="mt-3 break-words [overflow-wrap:anywhere] text-base leading-relaxed text-ink">
              “{item.review.reviewText}”
            </blockquote>
            <TechnicalDetails item={item} />
          </div>
        ))}
      </div>
    </article>
  );
}

function groupRecentReviewsByReviewer(items: RecentReview[]): Array<{ key: string; items: RecentReview[] }> {
  const groups = new Map<string, RecentReview[]>();
  for (const item of items) {
    const primary = preferredReviewerAccount(item.profileAccounts.length > 0 ? item.profileAccounts : item.review.reviewer.accounts);
    const key = primary ? `identity:${primary.handle}` : item.profileId ? `profile:${item.profileId}` : `wallet:${item.review.reviewer.wallet.toLowerCase()}`;
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return Array.from(groups, ([key, groupedItems]) => ({ key, items: groupedItems }));
}

function uniqueLinkedAccounts(accounts: X402LinkedAccount[]): X402LinkedAccount[] {
  const byHandle = new Map<string, X402LinkedAccount>();
  for (const account of accounts) byHandle.set(account.handle, account);
  return Array.from(byHandle.values());
}

export function RecentReviewCard({ item }: { item: RecentReview }) {
  const { review, profile } = item;
  const identityAccounts = item.profileAccounts.length > 0 ? item.profileAccounts : review.reviewer.accounts;
  const primary = preferredReviewerAccount(identityAccounts);
  const displayName = profile?.displayName || primary?.handle.replace(/^[^:]+:/, "") || shortAddr(review.reviewer.wallet);
  const profileHref = recentReviewerHref(item);

  return (
    <article className="min-w-0 overflow-hidden rounded-xl border border-faint bg-paper p-4 sm:p-5">
      {serviceEndpoint(item) ? <ServiceBrand endpoint={serviceEndpoint(item)!} compact /> : <span className="pill pill-faint btn-mono text-[10px]">Service review</span>}
      <Link
        className="mt-3 block min-w-0 break-words [overflow-wrap:anywhere] text-xl font-display tracking-tight text-ink hover:text-olive-deep hover:underline underline-offset-4"
        to={recentReviewRouteHref(item)}
      >
        {recentReviewTitle(item)}
      </Link>
      <blockquote className="mt-3 break-words [overflow-wrap:anywhere] text-base leading-relaxed text-ink">
        “{review.reviewText}”
      </blockquote>
      <div className="mt-5 flex min-w-0 items-center gap-3 border-t border-faint pt-4">
        <Link to={profileHref} aria-label={`Open ${displayName}'s profile`} className="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2">
          {profile?.avatarUrl ? (
            <img src={profile.avatarUrl} alt="" className="h-8 w-8 rounded-full border border-faint object-cover" />
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-olive/35 bg-olive-soft text-olive-deep" aria-hidden="true">
              <PersonGlyph className="h-4 w-4" />
            </span>
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Link className="min-w-0 break-words text-sm font-semibold text-ink hover:text-olive-deep hover:underline underline-offset-4" to={profileHref}>{displayName}</Link>
            <span className="flex max-w-full flex-wrap items-center gap-1" aria-label={item.profileId ? "Linked profile identities" : "Linked reviewer identities"}>
              {identityAccounts.map((account) => <ProviderBadge key={account.handle} account={account} showWallet={Boolean(item.profileId)} />)}
            </span>
            <span className="pill pill-olive btn-mono text-[10px]">{humanEvidenceLabel(review)}</span>
          </div>
          <p className="mt-1 truncate text-xs text-muted" title={primary?.handle ?? review.reviewer.wallet}>Reviewed by {reviewerLabel(item)} · {reviewDate(review)}</p>
        </div>
      </div>
      <TechnicalDetails item={item} />
    </article>
  );
}

function serviceEndpoint(item: X402RecentReviewItem): string | null {
  return item.route ? `${item.route.origin}${item.route.path}` : item.description?.route.publicEndpointUrl ?? null;
}

export function recentReviewTitle(item: X402RecentReviewItem): string {
  const endpoint = serviceEndpoint(item);
  const method = item.route?.method ?? item.description?.route.method ?? null;
  if (endpoint) {
    const knownDescription = method ? serviceDescription(endpoint, method) : null;
    if (knownDescription && method) return serviceName(endpoint, method, knownDescription);
    const summary = item.description?.summary?.trim();
    if (summary && !/^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b|https?:\/\//i.test(summary)) return summary;
    return serviceBrand(endpoint).name;
  }
  if (item.description?.summary) return item.description.summary;
  return "Service review";
}

function humanEvidenceLabel(review: X402RecentPublicReview): string {
  if (review.evidenceKind === "boon_backed") return "Boon-backed review";
  if (review.evidenceKind === "receipt_verified") return "Receipt-verified use";
  if (review.evidenceKind === "client_settlement") return "Payment-linked review";
  return "Self-reported review";
}

function reviewDate(review: X402RecentPublicReview): string {
  return review.publishedAt === null ? "date unavailable" : formatRelative(String(review.publishedAt));
}

function reviewerLabel(item: X402RecentReviewItem): string {
  const accounts = item.profileAccounts.length > 0 ? item.profileAccounts : item.review.reviewer.accounts;
  const primary = preferredReviewerAccount(accounts);
  if (primary) return primary.handle;
  return shortAddr(item.review.reviewer.wallet);
}

function TechnicalDetails({ item }: { item: X402RecentReviewItem }) {
  const endpoint = serviceEndpoint(item);
  if (!endpoint) return null;
  const method = item.route?.method ?? item.description?.route.method ?? null;
  return (
    <details className="mt-3 text-sm text-muted">
      <summary className="cursor-pointer min-h-9 inline-flex items-center btn-mono text-[10px] text-olive-deep">Technical details</summary>
      <p className="mt-1 break-all font-mono text-xs">{method ? <><span className="text-olive-deep">{method}</span>{" "}</> : null}{endpoint}</p>
      <p className="mt-2 text-xs leading-relaxed">{evidenceLimit(item.review)}</p>
    </details>
  );
}

function evidenceLimit(review: X402RecentPublicReview): string {
  if (review.evidenceKind === "client_settlement") return "Payment-linked activity supports that a wallet settlement was observed. It does not prove the review is true, the service was useful, or that the endpoint succeeded.";
  if (review.evidenceKind === "receipt_verified") return "Receipt evidence supports a payment receipt for the route. It does not prove the review is true, the service was useful, or that the response was good.";
  if (review.evidenceKind === "boon_backed") return "Recognition evidence supports a routed Boon tied to this review. It does not prove the review is true, the service was useful, or that the response was good.";
  return "This is a self-reported review. Boon does not independently verify the review text or service quality.";
}

function ProviderBadge({ account, showWallet }: { account: X402LinkedAccount; showWallet: boolean }) {
  const attribution = showWallet ? `${account.handle} · linked via ${shortAddr(account.linkedWallet)}` : account.handle;
  return (
    <Link
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-faint bg-paper text-muted hover:border-olive hover:text-olive-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2"
      title={attribution}
      aria-label={`Open ${attribution} profile`}
      to={`/p/${encodeURIComponent(account.handle)}`}
    >
      {account.provider === "x" ? <XMark className="h-3 w-3" /> : account.provider === "github" ? <GithubMark className="h-3.5 w-3.5" /> : <RobotGlyph className="h-3.5 w-3.5" />}
      <span className="sr-only">{account.handle}</span>
    </Link>
  );
}

type GlyphProps = { className?: string };
function PersonGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="8" r="3.25"/><path d="M5.5 20c.7-4 3-6 6.5-6s5.8 2 6.5 6"/></svg>; }
function RobotGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M12 3v3M8 3h8M5 9h14v10H5z"/><circle cx="9" cy="13" r="1" fill="currentColor" stroke="none"/><circle cx="15" cy="13" r="1" fill="currentColor" stroke="none"/><path d="M9 17h6"/></svg>; }
